import styled from '@emotion/styled';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import type { PropsWithChildren } from 'react';
import { useState, useMemo, useEffect } from 'react';
import ReactDOM from 'react-dom';
import type { UrlObject } from 'url';
import { layoutPadding } from '../layout-styles';
import ImageButton from '@/components/comm/ImageButton';
import { colors } from '@/constants/colors';
import { useBreakpoint } from '@/lib/breakpoint.service';
import icBack from '@/public/images/ic-back.svg';
import icClearBlack from '@/public/images/ic-clear-black.svg';

type MobileNavProps = {
  title?: string,
  icon: 'back' | 'close',
  href?: string | UrlObject,
};

export const mobileNavIdAtom = atom('');
export const pageTitleIdAtom = atom<string | null>(null);
export const pageTitleTextAtom = atom<string | null>(null);
const isMountMobileNavAtom = atom(false);

const Container = styled.div`
position: relative;
display: flex;
height: 48px;
align-items: center;
padding: ${layoutPadding};
`;

const NavIconWrapper = styled.div`
display: grid;
width: 24px;
height: 100%;
place-items: center;
`;

const NavTitle = styled.span`
position: absolute;
top: 50%;
left: 50%;
color: ${colors.textPrimary};
font-size: 16px;
font-weight: 700;
line-height: 100%;
transform: translate(-50%, -50%);
`;

const MobileNavPortal = ({ children }: PropsWithChildren) => {
  const mobileNavId = useAtomValue(mobileNavIdAtom);
  const container = useMemo(() => {
    if (typeof window === 'undefined') return null;
    return document.getElementById(mobileNavId);
  }, [mobileNavId]);

  if (process?.env.NODE_ENV === 'test') return <>{children}</>;

  return container && ReactDOM.createPortal(children, container);
};

const MobileNavBase = ({ children }: PropsWithChildren) => {
  const setIsMobileNavMount = useSetAtom(isMountMobileNavAtom);
  const isShowMobileNav = useIsShowMobileNav();

  useEffect(() => {
    setIsMobileNavMount(true);
    return () => setIsMobileNavMount(false);
  }, [setIsMobileNavMount]);

  return <MobileNavPortal>{isShowMobileNav && children}</MobileNavPortal>;
};

const MobileNav = ({
  title,
  icon,
  href,
}: MobileNavProps) => {
  const [showTitleByTrigger, setShowTitleByTrigger] = useState(true);
  const pageTitleId = useAtomValue(pageTitleIdAtom);
  const pageTitleText = useAtomValue(pageTitleTextAtom);
  const router = useRouter();

  const iconSrc = useMemo(() => {
    switch (icon) {
      case 'back': return icBack;
      case 'close': return icClearBlack;
    }
  }, [icon]);

  const navTitle = useMemo(
    () => title || pageTitleText,
    [pageTitleText, title],
  );

  useEffect(() => {
    if (typeof window === 'undefined' || !pageTitleId) return;
    const pageTitleElement = document.getElementById(pageTitleId);
    if (pageTitleElement) {
      const observer = new IntersectionObserver((entries) => {
        if (entries[0].intersectionRatio === 1) {
          setShowTitleByTrigger(false);
        } else {
          setShowTitleByTrigger(true);
        }
      }, { threshold: 1 });
      observer.observe(pageTitleElement);
      return () => {
        observer.disconnect();
      };
    }
  }, [pageTitleId]);

  return (
    <MobileNavBase>
      <Container>
        <NavIconWrapper
          onClick={() => {
            href && router.push(href) || router.back();
          }}
        >
          <ImageButton
            src={iconSrc}
            alt="mobile navigation button"
          />
        </NavIconWrapper>
        {navTitle && showTitleByTrigger && <NavTitle>{navTitle}</NavTitle>}
      </Container>
    </MobileNavBase>
  );
};

export const useIsShowMobileNav = () => {
  const breakpoint = useBreakpoint();
  const isMountMobileNav = useAtomValue(isMountMobileNavAtom);
  const isNotLg = useMemo(() => breakpoint !== 'lg', [breakpoint]);

  return useMemo(() => isNotLg && isMountMobileNav, [isNotLg, isMountMobileNav]);
};

export default MobileNav;
